<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useCrmStore } from '../store';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import WhatsappTemplates from 'dashboard/components/widgets/conversation/WhatsappTemplates/Modal.vue';
import WindowHint from 'dashboard/wasapi/common/WindowHint.vue';

const emit = defineEmits(['save']);

const { t } = useI18n();
const store = useCrmStore();
const inboxes = useMapGetter('inboxes/getInboxes');
const agents = useMapGetter('agents/getVerifiedAgents');
const teams = useMapGetter('teams/getTeams');
const labels = useMapGetter('labels/getLabels');
const inboxGetter = useMapGetter('inboxes/getInbox');

const dialogRef = ref(null);
const stage = ref(null);
const inboxId = ref('');
const actions = ref([]);
const templateFor = ref(null);

const ACTION_TYPES = [
  'send_message',
  'send_template',
  'assign_agent',
  'assign_team',
  'move_stage',
  'ensure_conversation',
  'add_label',
];

const typeOptions = computed(() =>
  ACTION_TYPES.map(type => ({
    value: type,
    label: t(`WASAPI_CRM.AUTOMATIONS.TYPES.${type.toUpperCase()}`),
  }))
);
const inboxOptions = computed(() => [
  { value: '', label: '—' },
  ...(inboxes.value || []).map(i => ({ value: i.id, label: i.name })),
]);
const agentOptions = computed(() =>
  (agents.value || []).map(a => ({ value: a.id, label: a.name }))
);
const teamOptions = computed(() =>
  (teams.value || []).map(te => ({ value: te.id, label: te.name }))
);
const labelOptions = computed(() =>
  (labels.value || []).map(l => ({ value: l.title, label: l.title }))
);
const stageGroups = computed(() =>
  store.pipelines.map(p => ({
    label: p.name,
    options: p.stages
      .filter(s => s.id !== stage.value?.id)
      .map(s => ({ value: s.id, label: s.name })),
  }))
);
// O ComboBox é uma lista única: o funil vira prefixo do rótulo, então a busca acha
// tanto por etapa quanto por funil.
const stageOptions = computed(() =>
  stageGroups.value.flatMap(group =>
    group.options.map(option => ({
      value: option.value,
      label: `${group.label} · ${option.label}`,
    }))
  )
);
const isWhatsAppInbox = computed(
  () => inboxGetter.value(inboxId.value)?.channel_type === 'Channel::Whatsapp'
);

const open = stageData => {
  stage.value = stageData;
  inboxId.value = stageData.inboxId || '';
  actions.value = (stageData.automations || []).map(a => ({ ...a }));
  dialogRef.value?.open();
};

const addAction = () =>
  actions.value.push({ type: 'send_message', content: '' });
const removeAction = index => actions.value.splice(index, 1);

const onTemplatePicked = ({ templateParams }) => {
  if (templateFor.value !== null) {
    actions.value[templateFor.value].template_params = templateParams;
  }
  templateFor.value = null;
};

const save = () => {
  emit('save', {
    stageId: stage.value.id,
    inboxId: inboxId.value || null,
    automations: actions.value.map(a => {
      const out = { type: a.type };
      if (a.type === 'send_message') out.content = a.content;
      if (a.type === 'send_template') out.template_params = a.template_params;
      if (a.type === 'assign_agent') out.user_id = a.user_id;
      if (a.type === 'assign_team') out.team_id = a.team_id;
      if (a.type === 'move_stage') out.stage_id = a.stage_id;
      if (a.type === 'add_label') out.label = a.label;
      return out;
    }),
  });
  dialogRef.value?.close();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="t('WASAPI_CRM.AUTOMATIONS.TITLE', { stage: stage?.name || '' })"
    :description="t('WASAPI_CRM.AUTOMATIONS.LOOP_HINT')"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">{{
          t('WASAPI_CRM.AUTOMATIONS.INBOX')
        }}</span>
        <ComboBox v-model="inboxId" :options="inboxOptions" class="w-full" />
      </div>

      <p v-if="!actions.length" class="text-sm text-n-slate-11">
        {{ t('WASAPI_CRM.AUTOMATIONS.EMPTY') }}
      </p>
      <div
        v-for="(action, index) in actions"
        :key="index"
        class="flex flex-col gap-2 p-3 rounded-xl outline outline-1 outline-n-weak"
      >
        <div class="flex items-center gap-2">
          <span class="text-xs text-n-slate-10 w-5">{{ index + 1 }}.</span>
          <ComboBox
            v-model="action.type"
            :options="typeOptions"
            class="flex-1"
          />
          <Button
            icon="i-lucide-trash-2"
            variant="ghost"
            color="ruby"
            size="sm"
            @click="removeAction(index)"
          />
        </div>
        <template v-if="action.type === 'send_message'">
          <TextArea
            v-model="action.content"
            :placeholder="t('WASAPI_CRM.AUTOMATIONS.CONTENT')"
            class="w-full"
            auto-height
          />
          <WindowHint v-if="isWhatsAppInbox" />
        </template>
        <div
          v-else-if="action.type === 'send_template'"
          class="flex items-center gap-2 text-sm"
        >
          <span v-if="action.template_params" class="text-n-slate-12">
            {{
              t('WASAPI_CRM.AUTOMATIONS.TEMPLATE', {
                name: action.template_params.name,
              })
            }}
          </span>
          <Button
            :label="t('WASAPI_CRM.AUTOMATIONS.PICK_TEMPLATE')"
            icon="i-ph-whatsapp-logo"
            variant="faded"
            color="slate"
            size="sm"
            :disabled="!isWhatsAppInbox"
            @click="templateFor = index"
          />
          <span
            v-if="!isWhatsAppInbox"
            class="text-xs text-n-slate-10 flex items-center gap-1"
          >
            <Icon icon="i-lucide-info" class="size-3.5" />{{
              t('WASAPI_CRM.AUTOMATIONS.INBOX')
            }}
          </span>
        </div>
        <ComboBox
          v-else-if="action.type === 'assign_agent'"
          v-model="action.user_id"
          :options="agentOptions"
          :placeholder="t('WASAPI_CRM.AUTOMATIONS.AGENT')"
        />
        <ComboBox
          v-else-if="action.type === 'assign_team'"
          v-model="action.team_id"
          :options="teamOptions"
          :placeholder="t('WASAPI_CRM.AUTOMATIONS.TEAM')"
        />
        <ComboBox
          v-else-if="action.type === 'move_stage'"
          v-model="action.stage_id"
          :options="stageOptions"
          :placeholder="t('WASAPI_CRM.AUTOMATIONS.STAGE')"
        />
        <ComboBox
          v-else-if="action.type === 'add_label'"
          v-model="action.label"
          :options="labelOptions"
          :placeholder="t('WASAPI_CRM.AUTOMATIONS.LABEL')"
        />
      </div>
      <Button
        :label="t('WASAPI_CRM.AUTOMATIONS.ADD')"
        icon="i-lucide-plus"
        variant="faded"
        color="slate"
        size="sm"
        class="self-start"
        @click="addAction"
      />
    </div>
    <template #footer>
      <div class="flex items-center justify-end w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="faded"
          color="slate"
          @click="dialogRef.close()"
        />
        <Button
          :label="t('WASAPI_CRM.AUTOMATIONS.SAVE')"
          color="blue"
          @click="save"
        />
      </div>
    </template>
    <WhatsappTemplates
      v-if="isWhatsAppInbox"
      :inbox-id="Number(inboxId)"
      :show="templateFor !== null"
      @on-send="onTemplatePicked"
      @cancel="templateFor = null"
      @update:show="
        value => {
          if (!value) templateFor = null;
        }
      "
    />
  </Dialog>
</template>
